import { HttpClient } from 'api/http-client';
import { IntegrationField } from '../types';
import {
  HubspotConfig,
  HubspotFieldMapping,
  HubspotIntegrationProvider,
  HubspotObject,
  HubspotResourceMapping,
} from './types';

const base = '/integrations/hubspot';

class HubspotHTTPClient extends HttpClient {
  public getAuthDetails = (): Promise<HubspotIntegrationProvider> =>
    this.get<HubspotIntegrationProvider>('/integration-providers/hubspot/meta');

  public disconnectIntegration = (integrationId: number): Promise<void> =>
    this.delete<void>(`${base}/${integrationId}`);

  public getIntegrationConfig = (): Promise<HubspotConfig[]> =>
    this.get<HubspotConfig[]>(base);

  public getIntegrationConfigById = (
    integrationId: number,
  ): Promise<HubspotConfig> =>
    this.get<HubspotConfig>(`${base}/${integrationId}`);

  public getResourceMappings = (
    integrationId: number,
  ): Promise<HubspotResourceMapping[]> =>
    this.get<HubspotResourceMapping[]>(
      `${base}/${integrationId}/resource-mapping-config`,
    );

  public getExternalResourceTypes = (
    integrationId: number,
  ): Promise<HubspotObject[]> =>
    this.get<HubspotObject[]>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types`,
    );

  public getExternalResourceFields = (
    integrationId: number,
    externalResourceType: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types/${externalResourceType}/fields`,
    );

  public getNativeResourceTypes = (
    integrationId: number,
  ): Promise<HubspotResourceMapping[]> =>
    this.get<HubspotResourceMapping[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types`,
    );

  public getMappableNativeResourceFields = (
    integrationId: number,
    nativeResourceType: string,
    externalResourceType: string,
    externalResourceFieldId: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types/${nativeResourceType}/mappable-fields?externalResourceType=${externalResourceType}&externalFieldId=${externalResourceFieldId}`,
    );

  public createFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    payload: HubspotFieldMapping,
  ): Promise<HubspotFieldMapping> =>
    this.post(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config`,
      payload,
    );

  public deleteFieldMapping = (
    integrationId: number,
    resourceId: number,
    fieldMappingId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}/field-mapping-config/${fieldMappingId}`,
    );

  public updateFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    fieldMappingId: number,
    payload: HubspotFieldMapping,
  ): Promise<HubspotFieldMapping> =>
    this.put(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config/${fieldMappingId}`,
      payload,
    );

  public createResourceObject = (
    integrationId: number,
    payload: AtLeast<
      HubspotResourceMapping,
      | 'externalIntegrationType'
      | 'externalResourceType'
      | 'nativeResourceType'
      | 'syncType'
      | 'enabled'
    >,
  ): Promise<HubspotResourceMapping> =>
    this.post(`${base}/${integrationId}/resource-mapping-config/`, payload);

  public deleteResourceObject = (
    integrationId: number,
    resourceId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}`,
    );
}

export const HubspotAPI = new HubspotHTTPClient();
